import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const FounderBioEditor = ({ userId }) => {
  const [founders, setFounders] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', role: '', bio: '' });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    loadFounders();
  }, []);

  const loadFounders = async () => {
    try {
      const { data, error } = await supabase?.from('founders')?.select('*')?.order('display_order', { ascending: true });

      if (error) throw error;
      setFounders(data || []);
    } catch (error) {
      console.error('Error loading founders:', error);
      setError('Failed to load founders');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (founder) => {
    setEditingId(founder?.id);
    setEditForm({
      name: founder?.name,
      role: founder?.role,
      bio: founder?.bio
    });
    setError(null);
    setSuccess(null);
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditForm({ name: '', role: '', bio: '' });
    setError(null);
  };

  const handleSave = async (founderId) => {
    if (!editForm?.name?.trim() || !editForm?.role?.trim() || !editForm?.bio?.trim()) {
      setError('All fields are required');
      return;
    }

    try {
      const { error: updateError } = await supabase?.from('founders')?.update({
          name: editForm?.name?.trim(),
          role: editForm?.role?.trim(),
          bio: editForm?.bio?.trim(),
          updated_by: userId
        })?.eq('id', founderId);

      if (updateError) throw updateError;

      setSuccess('Founder bio updated successfully');
      setEditingId(null);
      setEditForm({ name: '', role: '', bio: '' });
      loadFounders();

      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Update error:', error);
      setError(`Failed to update: ${error?.message}`);
    }
  };

  if (isLoading) {
    return (
      <div className="modern-card">
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div>
          <p className="text-muted-foreground mt-2">Loading founders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="modern-card">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-blue-100 rounded-lg">
          <Icon name="Users" size={24} className="text-blue-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-foreground">Founder Bios</h2>
          <p className="text-sm text-muted-foreground">
            Update founder information displayed on the homepage
          </p>
        </div>
      </div>
      {/* Error/Success Messages */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
          <Icon name="AlertCircle" size={18} className="text-red-600" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
          <Icon name="CheckCircle" size={18} className="text-green-600" />
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}
      {/* Founders List */}
      <div className="space-y-6">
        {founders?.map((founder) => (
          <div key={founder?.id} className="border border-border rounded-lg p-6">
            {editingId === founder?.id ? (
              // Edit Mode
              (<div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    value={editForm?.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e?.target?.value })}
                    className="input-field"
                    placeholder="Enter founder name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Role
                  </label>
                  <input
                    type="text"
                    value={editForm?.role}
                    onChange={(e) => setEditForm({ ...editForm, role: e?.target?.value })}
                    className="input-field"
                    placeholder="Enter role/title"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Bio
                  </label>
                  <textarea
                    value={editForm?.bio}
                    onChange={(e) => setEditForm({ ...editForm, bio: e?.target?.value })}
                    rows={5}
                    className="input-field resize-none"
                    placeholder="Enter founder bio"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {editForm?.bio?.length} characters
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => handleSave(founder?.id)}
                    className="btn-primary"
                  >
                    <Icon name="Check" size={18} />
                    Save Changes
                  </button>
                  <button
                    onClick={handleCancel}
                    className="btn-secondary"
                  >
                    <Icon name="X" size={18} />
                    Cancel
                  </button>
                </div>
              </div>)
            ) : (
              // View Mode
              (<div>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <img
                        src={founder?.avatar_url}
                        alt={`${founder?.name} profile`}
                        className="w-20 h-20 rounded-full object-cover border-2 border-border"
                      />
                      {founder?.initials && (
                        <div className={`absolute -bottom-1 -right-1 w-8 h-8 ${founder?.bg_color || 'bg-primary'} rounded-full flex items-center justify-center text-white font-bold text-xs border-2 border-background`}>
                          {founder?.initials}
                        </div>
                      )}
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-foreground">{founder?.name}</h3>
                      <p className="text-sm text-purple-400 font-medium">{founder?.role}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleEdit(founder)}
                    className="btn-secondary"
                  >
                    <Icon name="Edit2" size={18} />
                    Edit
                  </button>
                </div>
                <p className="text-muted-foreground leading-relaxed">{founder?.bio}</p>
                <div className="mt-4 pt-4 border-t border-border flex items-center gap-4 text-xs text-muted-foreground">
                  <span>Order: #{founder?.display_order}</span>
                  {founder?.updated_at && (
                    <span>Last updated: {new Date(founder.updated_at)?.toLocaleDateString()}</span>
                  )}
                </div>
              </div>)
            )}
          </div>
        ))}
      </div>
      {founders?.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <Icon name="Users" size={48} className="mx-auto mb-3 opacity-30" />
          <p>No founders found</p>
        </div>
      )}
      {/* Instructions */}
      <div className="mt-6 p-4 bg-accent/10 border border-accent/20 rounded-lg">
        <div className="flex items-start gap-2">
          <Icon name="Info" size={18} className="text-accent flex-shrink-0 mt-0.5" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-1">Guidelines:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Keep bios concise and engaging (2-3 sentences recommended)</li>
              <li>Highlight relevant experience and expertise</li>
              <li>Maintain consistent tone and style across all founder bios</li>
              <li>Changes are immediately visible on the homepage</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FounderBioEditor;